import { useEffect } from "react";
import { gtm } from "src/libs/gtm";
import useUserDataProvider from "src/contexts/snug/userData-context";

/**
 * Custom hook to track Calendly demo bookings with GTM
 * Listens for Calendly's postMessage events and fires GTM events (browser + backend)
 * when a demo is successfully scheduled
 */
export const useGtmCalendly = () => {
  const { userData } = useUserDataProvider();

  useEffect(() => {
    const handleCalendlyMessage = (event: MessageEvent) => {
      // Validate origin is from Calendly
      if (!event.origin.includes("calendly.com")) return;

      let data;
      try {
        // Parse the data if it's a string
        data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch {
        // Invalid JSON, ignore
        return;
      }

      // Listen for event_scheduled event (fired when booking is completed)
      if (data.event === "calendly.event_scheduled") {
        // Prepare user data for GTM tracking
        const gtmUserData = userData ? {
          email: userData.contact_email || undefined,
          firstName: userData.full_name?.split(' ')[0] || undefined,
          lastName: userData.full_name?.split(' ').slice(1).join(' ') || undefined,
          ud_id: userData.ud_id || undefined,
        } : undefined;

        // Generate deterministic event_id to prevent duplicates across multiple hook instances
        // Use Calendly invitee URI (unique per booking) or fallback to ud_id
        const calendlyInviteeUri = data.data?.invitee?.uri;
        const eventId = calendlyInviteeUri
          ? `demobook-${calendlyInviteeUri.split('/').pop()}` // Extract last part of URI
          : `demobook-${userData?.ud_id || 'unknown'}`;

        // Fire GTM event with both browser (dataLayer) and backend (API) tracking
        gtm.pushWithBackend({
          event: "demo_booking_completed",
          event_id: eventId,
          calendly_event_uri: data.data?.event?.uri || undefined,
          calendly_invitee_uri: calendlyInviteeUri || undefined,
        }, gtmUserData);
      }
    };

    // Listen for postMessage events from Calendly
    window.addEventListener("message", handleCalendlyMessage);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener("message", handleCalendlyMessage);
    };
  }, [userData]);
};
